import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, useTheme } from '@mui/material';
import { LightbulbOutlined as LightBulbIcon } from '@mui/icons-material';
import {
  DianaFeaturesEnum,
  ImportAssetModal,
  EmptyAssetsIllustration,
  EmptyAssetsMobileIllustration,
  EmptyView,
  IEmptyViewAction,
  getDeviceType,
  GlobalAddModalEnum,
  useAppStore,
  useHasFeature,
  useImportAssetModal,
} from '@diana/core';
import { THINGS_GROUP_PAGE_NS } from './i18n';

function AssetsIllustration() {
  const { isMobile } = getDeviceType();

  if (isMobile)
    return <EmptyAssetsMobileIllustration sx={{ maxWidth: '100%', maxHeight: '100%' }} />;

  return <EmptyAssetsIllustration sx={{ maxWidth: '100%', maxHeight: '100%' }} />;
}

export function ThingsGroupPageEmptyView() {
  const { t } = useTranslation(THINGS_GROUP_PAGE_NS);
  const { palette } = useTheme();
  const { isMobile } = getDeviceType();

  const setModal = useAppStore((store) => store.setGlobalAddModal);
  const canCreateAssetType = useHasFeature(DianaFeaturesEnum.AssetCreate);

  const {
    isImportAssetModalOpen,
    setImportAssetModalOpen,
    isLoadingImportAsset,
    onImportAsset,
    importErrors,
    getAssetTypeNameAvailability,
    getAssetTypeLayoutByFlowOriginId,
  } = useImportAssetModal();

  const getActions = () => {
    const actions: IEmptyViewAction[] = [];

    if (canCreateAssetType) {
      if (!isMobile)
        actions.push({
          label: t('buttons.buildNewAssetType'),
          color: 'secondary',
          onClick: () => setModal(GlobalAddModalEnum.Things),
        });

      actions.push({
        label: t('buttons.importAssets'),
        color: 'primary',
        onClick: () => setImportAssetModalOpen(true),
      });
    }

    return actions;
  };

  return (
    <Fragment>
      <EmptyView
        illustration={<AssetsIllustration />}
        title={getActions().length ? t('titles.addFirstAssetType') : ''}
        description={getActions().length ? t('descriptions.createAssetType') : undefined}
        actionsDirection="row"
        actions={getActions()}
        tip={{
          icon: (
            <Avatar
              sx={{
                width: 24,
                height: 24,
                color: palette.primary.contrastText,
                backgroundColor: palette.primary.main,
              }}
            >
              <LightBulbIcon sx={{ fontSize: '16px' }} color="inherit" />
            </Avatar>
          ),
          description: t('descriptions.checkKnowledgeBase'),
          link: {
            label: t('descriptions.checkKnowledgeBase'),
            href: 'https://help.rise-x.io/',
            target: '_blank',
            isIcon: true,
          },
        }}
      />
      <ImportAssetModal
        isOpen={isImportAssetModalOpen}
        onRequestClose={() => setImportAssetModalOpen(false)}
        isLoading={isLoadingImportAsset}
        onClickImport={onImportAsset}
        importErrors={importErrors}
        getAssetTypeNameAvailability={getAssetTypeNameAvailability}
        getAssetTypeLayoutByFlowOriginId={getAssetTypeLayoutByFlowOriginId}
      />
    </Fragment>
  );
}
